import type {
  ContactInfo,
  Education,
  ProfessionalSummaryEntry,
  SkillCategory,
} from '@applicator/shared'
import {
  contactExtraLinkValues,
  contactFieldValue,
} from '@applicator/shared'

import type { QualityIssue } from './qualityEvalFlagging'
import type { SkillGapSummary, SkillPreMatch } from './skillGapRecommendations'
import { groupSkillGapByResumeSection } from './skillGapRecommendations'
import {
  summaryHasAiFocusSignals,
  summarySatisfiesRecommendation,
} from './summaryFeedbackRequirements'
import { isEducationDateRangeInvalid } from '../../utils/educationDateValidation'

export interface FeedbackProgressContext {
  contactInfo: ContactInfo
  education: Education[]
  professionalSummary: ProfessionalSummaryEntry[]
  skills: SkillCategory[]
  jobDescription?: string
}

type ContactFieldKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'location'
  | 'linkedin'

function contactStored(contact: ContactInfo, field: ContactFieldKey): boolean {
  const value = contact[field]
  return typeof value === 'string' && value.trim().length > 0
}

function contactOnResume(contact: ContactInfo, field: ContactFieldKey): boolean {
  return contactFieldValue(contact, field).length > 0
}

function classifyUrlKind(url: string): string {
  const lowered = url.toLowerCase()
  if (lowered.includes('linkedin.com')) return 'linkedin'
  if (lowered.includes('github.com') || lowered.includes('gitlab.com')) return 'github'
  if (lowered.includes('substack.com')) return 'substack'
  if (lowered.includes('twitter.com') || lowered.includes('x.com/')) return 'twitter'
  return 'other_url'
}

function linkKindsOnResume(contact: ContactInfo): Set<string> {
  const kinds = new Set<string>()
  const extra = contact as ContactInfo & Record<string, string | undefined>

  if (contactOnResume(contact, 'linkedin')) kinds.add('linkedin')
  if (contactOnResume(contact, 'substack')) kinds.add('substack')
  if (contactOnResume(contact, 'github')) kinds.add('github')

  // Legacy flat fields (no *Included flags): non-empty value counts as on-resume.
  const website = String(extra.website ?? '').trim()
  if (website) kinds.add(classifyUrlKind(website))
  const portfolio = String(extra.portfolio ?? '').trim()
  if (portfolio) kinds.add('portfolio')
  const twitter = String(extra.twitter ?? '').trim()
  if (twitter) kinds.add(classifyUrlKind(twitter))

  for (const url of contactExtraLinkValues(contact)) {
    kinds.add(classifyUrlKind(url))
  }
  return kinds
}

function ch1MissingCoreLabels(contact: ContactInfo): string[] {
  const missing: string[] = []
  if (!contactOnResume(contact, 'firstName')) missing.push('first name')
  if (!contactOnResume(contact, 'lastName')) missing.push('last name')
  if (!contactOnResume(contact, 'email')) missing.push('email')
  if (!contactOnResume(contact, 'phone')) missing.push('phone')
  if (!contactOnResume(contact, 'location')) missing.push('location')
  if (!contactOnResume(contact, 'linkedin')) missing.push('LinkedIn')
  return missing
}

function ch1ForbiddenOnResume(contact: ContactInfo): string[] {
  const kinds = linkKindsOnResume(contact)
  return [...kinds].filter((k) => k === 'substack' || k === 'github')
}

export function isCh1ContactSatisfied(contact: ContactInfo): boolean {
  return ch1MissingCoreLabels(contact).length === 0 && ch1ForbiddenOnResume(contact).length === 0
}

function includedEducationEntries(education: Education[]): Education[] {
  return education.filter((row) => row.included !== false)
}

function educationOnResume(education: Education[]): boolean {
  return includedEducationEntries(education).length > 0
}

function educationInstitutionsComplete(education: Education[]): boolean {
  const included = includedEducationEntries(education)
  if (!included.length) return false
  return included.every((row) => row.institution?.trim())
}

function educationDatesValid(education: Education[]): boolean {
  const included = includedEducationEntries(education)
  return included.every(
    (row) => !isEducationDateRangeInvalid(row.startDate, row.endDate)
  )
}

function includedSummaryText(entries: ProfessionalSummaryEntry[]): string {
  return entries
    .filter((e) => e.included !== false && e.text?.trim())
    .map((e) => e.text.trim())
    .join('\n')
}

function skillNamesOnResume(skills: SkillCategory[]): string[] {
  const names: string[] = []
  for (const cat of skills) {
    for (const skill of cat.skills ?? []) {
      if (skill.included !== false && skill.name?.trim()) {
        names.push(skill.name.trim())
      }
    }
  }
  return names
}

function skillKeywordPresent(skills: SkillCategory[], keyword: string): boolean {
  const needle = keyword.trim().toLowerCase()
  if (!needle) return false
  return skillNamesOnResume(skills).some(
    (name) => name.toLowerCase().includes(needle) || needle.includes(name.toLowerCase())
  )
}

function jdMentionsOfficeLocation(jobDescription: string): boolean {
  return /\b(on[- ]site|in[- ]office|office[- ]based|based in|relocation|hybrid)\b/i.test(
    jobDescription
  )
}

/** Mirror API build_contact_recommendations_heuristic — true when user fixed the item. */
export function isContactRecommendationResolved(
  text: string,
  ctx: FeedbackProgressContext
): boolean {
  const { contactInfo: contact, jobDescription = '' } = ctx
  const lower = text.toLowerCase()
  const linksOn = linkKindsOnResume(contact)

  if (/full name/i.test(lower)) {
    return contactOnResume(contact, 'firstName') && contactOnResume(contact, 'lastName')
  }
  if (/add an email/i.test(lower)) return contactStored(contact, 'email')
  if (/enable email/i.test(lower)) return contactOnResume(contact, 'email')
  if (/phone number/i.test(lower)) {
    if (/add a phone/i.test(lower)) return contactStored(contact, 'phone')
    if (/enable phone/i.test(lower)) return contactOnResume(contact, 'phone')
    return contactOnResume(contact, 'phone')
  }
  if (/location/i.test(lower) && /contact/i.test(lower)) {
    if (/add city/i.test(lower)) return contactStored(contact, 'location')
    if (/show location/i.test(lower)) return contactOnResume(contact, 'location')
  }
  if (/linkedin/i.test(lower)) {
    return contactOnResume(contact, 'linkedin') || linksOn.has('linkedin')
  }
  if (/remove github/i.test(lower)) return !linksOn.has('github')
  if (/substack|newsletter/i.test(lower)) {
    if (/github/i.test(lower)) {
      return !linksOn.has('substack') && !linksOn.has('github')
    }
    return !linksOn.has('substack')
  }
  if (/portfolio link/i.test(lower)) return !linksOn.has('portfolio')
  if (/personal social|twitter/i.test(lower)) return !linksOn.has('twitter')
  if (/extra links/i.test(lower)) {
    return !linksOn.has('other_url')
  }
  if (/enable it if the jd expects hands-on engineering/i.test(lower)) {
    return linksOn.has('github')
  }
  if (jdMentionsOfficeLocation(jobDescription) && /location/i.test(lower)) {
    return contactOnResume(contact, 'location')
  }

  return false
}

function isQualityIssueResolved(issue: QualityIssue, ctx: FeedbackProgressContext): boolean {
  const { contactInfo, education, professionalSummary, skills } = ctx

  if (issue.id.startsWith('edu:')) {
    const code = issue.id.slice('edu:'.length)
    if (code === 'education_missing') return educationOnResume(education)
    if (code === 'education_none_included') return educationOnResume(education)
    if (code === 'education_missing_institution') return educationInstitutionsComplete(education)
    if (code === 'education_end_before_start') return educationDatesValid(education)
    return false
  }

  if (issue.id.startsWith('sum:')) {
    const code = issue.id.slice('sum:'.length)
    const text = includedSummaryText(professionalSummary)
    if (code === 'summary_empty') return text.length > 0
    if (code === 'summary_too_long') return text.length > 0 && text.length < 600
    if (code === 'summary_em_dash') return text.length > 0 && !text.includes('—')
    return false
  }

  if (issue.id.startsWith('gate:CH1:')) {
    return isCh1ContactSatisfied(contactInfo)
  }

  if (issue.id.startsWith('gate:PS5:')) {
    const text = includedSummaryText(professionalSummary)
    if (summarySatisfiesRecommendation(issue.recommendation, text)) return true
    return text.length > 0 && summaryHasAiFocusSignals(text)
  }

  if (issue.id.startsWith('gate:PS8:')) {
    const text = includedSummaryText(professionalSummary)
    return summarySatisfiesRecommendation(issue.recommendation, text)
  }

  if (issue.id.startsWith('gate:')) {
    const gateId = issue.id.split(':')[1]?.toUpperCase() ?? ''
    const text = includedSummaryText(professionalSummary)

    if (
      gateId.startsWith('PS') &&
      summarySatisfiesRecommendation(issue.recommendation, text)
    ) {
      return true
    }

    if (gateId === 'PS3') {
      const banned =
        /\b(pivotal|delve|testament|underscore|i am drawn to)\b/i.test(text) ||
        /\b(?:english\s+b2|b2\s+english)\b/i.test(text)
      const proseArtifacts =
        text.includes(';') ||
        /\bincluding\s+including\b/i.test(text) ||
        /,\s+and\s+and\b/i.test(text) ||
        /\band\s+and\.\s*$/i.test(text)
      return (
        text.length > 0 &&
        text.length < 600 &&
        !text.includes('—') &&
        !banned &&
        !proseArtifacts
      )
    }

    if (gateId === 'PS10') {
      return text.length > 0 && text.length <= 1800
    }

    if (gateId === 'PS16') {
      return (
        text.length > 0 &&
        /\b(?:manag(?:e|ing|ed)|coach(?:ing|ed)|mentor(?:ing|ed)|led\s+(?:a\s+)?team|people\s+management)\b/i.test(
          text
        )
      )
    }

    if (gateId === 'PS17') {
      return (
        text.length > 0 &&
        /\b(?:financial\s+data|fintech|investment|data\s+warehouse|data\s+platforms?|redshift|bi\b|enterprise\s+data|analytics\s+platform|data-intensive)\b/i.test(
          text
        )
      )
    }

    if (gateId === 'PS18') {
      const teamAi =
        /\b(?:team|pm\s+team|product\s+managers?|across\s+teams?)\b.*\b(?:ai|llm|genai|automation)\b/i.test(
          text
        ) ||
        /\b(?:ai|llm|genai)\b.*\b(?:team|pm\s+team|coach|enablement|adoption)\b/i.test(text)
      const soloOnly =
        /\b(?:built\s+(?:my\s+)?own|personally\s+built|n8n|langchain|rag\s+pipeline)\b/i.test(
          text
        ) && !teamAi
      return text.length > 0 && teamAi && !soloOnly
    }

    if (gateId.startsWith('SK')) {
      const match = issue.recommendation.match(/«([^»]+)»/)
      if (match?.[1]) return skillKeywordPresent(skills, match[1])
    }
  }

  return false
}

export function summaryPassesWritingChecks(
  professionalSummary: ProfessionalSummaryEntry[]
): boolean {
  const text = includedSummaryText(professionalSummary)
  if (!text) return false
  const banned =
    /\b(pivotal|delve|testament|underscore|i am drawn to)\b/i.test(text) ||
    /\b(?:english\s+b2|b2\s+english)\b/i.test(text)
  const proseArtifacts =
    text.includes(';') ||
    /\bincluding\s+including\b/i.test(text) ||
    /,\s+and\s+and\b/i.test(text) ||
    /\band\s+and\.\s*$/i.test(text)
  return text.length < 600 && !text.includes('—') && !banned && !proseArtifacts
}

export function filterQualityIssuesByProgress(
  issues: QualityIssue[],
  ctx: FeedbackProgressContext
): QualityIssue[] {
  return issues.filter((issue) => !isQualityIssueResolved(issue, ctx))
}

export function filterContactRecommendationsByProgress(
  recommendations: string[],
  ctx: FeedbackProgressContext
): string[] {
  return recommendations.filter((rec) => !isContactRecommendationResolved(rec, ctx))
}

export function filterSkillGapRecommendations(
  recommendations: string[],
  skills: SkillCategory[]
): string[] {
  return recommendations.filter((rec) => {
    const redMatch = rec.match(/The job mentions «([^»]+)»/)
    if (redMatch?.[1]) return !skillKeywordPresent(skills, redMatch[1])
    const yellowMatch = rec.match(/^«([^»]+)» is on your resume/)
    if (yellowMatch?.[1]) return true
    return true
  })
}

export function filterSkillGapSummary(
  summary: SkillGapSummary | null,
  skills: SkillCategory[]
): SkillGapSummary | null {
  if (!summary) return null
  const red = summary.preMatch.red.filter((k) => !skillKeywordPresent(skills, k))
  const yellow = summary.preMatch.yellow
  const sectionGroups =
    summary.sectionGroups.length > 0
      ? summary.sectionGroups
          .map((g) => ({
            ...g,
            items:
              g.tone === 'red'
                ? g.items.filter((k) => !skillKeywordPresent(skills, k))
                : g.items,
          }))
          .filter((g) => g.items.length > 0)
      : groupSkillGapByResumeSection({ ...summary.preMatch, red, yellow })
  if (!red.length && !yellow.length && !sectionGroups.some((g) => g.tone !== 'green')) {
    if (!summary.fallbackMessage) return null
  }
  const preMatch: SkillPreMatch = { ...summary.preMatch, red, yellow }
  return {
    ...summary,
    preMatch,
    recommendations: [],
    greenCount: preMatch.green.length,
    sectionGroups,
  }
}

export function feedbackProgressContextFromResumeData(data: {
  contact_info?: ContactInfo
  education?: Education[]
  professional_summary?: ProfessionalSummaryEntry[]
  skills?: SkillCategory[]
}): FeedbackProgressContext {
  return {
    contactInfo: data.contact_info ?? ({} as ContactInfo),
    education: data.education ?? [],
    professionalSummary: data.professional_summary ?? [],
    skills: data.skills ?? [],
  }
}
